import { useState, type FormEvent } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2, Eye, EyeOff, Monitor } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'
import { Checkbox } from '@meno/ui/components/ui/checkbox'
import { LanguageToggle } from '@meno/ui/components/shared/LanguageToggle'
import { useAuth } from '@meno/ui/contexts/AuthContext'

export default function LoginPage() {
  const { t } = useTranslation()
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email,    setEmail]    = useState((location.state as any)?.email ?? '')
  const [password, setPassword] = useState('')
  const [orgNo,    setOrgNo]    = useState('')
  const [remember, setRemember] = useState(false)
  const [showPwd,  setShowPwd]  = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password, orgNo.trim(), remember)
      navigate('/overview', { replace: true })
    } catch (err: any) {
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      // 401/422 → bad credentials or validation. 5xx → server error (already
      // scrubbed by the api interceptor, but we localise the message here).
      if (status === 401 || status === 422) {
        setError(t('auth.invalidCredentials'))
      } else if (!status || status >= 500) {
        setError(t('auth.serverError', 'Something went wrong. Please try again.'))
      } else {
        setError(detail || t('auth.invalidCredentials'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel (brand) ──────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12"
        style={{ background: '#0c0f0a' }}
      >
        <div className="flex items-center">
          <img src="/favicon.svg" alt="WeTook" className="h-10 w-auto" />
        </div>

        <div className="text-white space-y-4">
          <h1 className="text-4xl font-bold leading-tight whitespace-pre-wrap">
            {t('auth.heroTitle')}
          </h1>
          <p className="text-white/75 text-lg">
            {t('auth.heroSubtitle')}
          </p>
        </div>

        <div className="flex gap-6 text-white/60 text-sm">
          <span>{t('nav.screens')}</span>
          <span>{t('nav.media')}</span>
          <span>{t('auth.analytics')}</span>
        </div>
      </div>

      {/* ── Right panel (form) ──────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background relative">
        <div className="absolute top-4 end-4">
          <LanguageToggle />
        </div>
        <div className="w-full max-w-sm space-y-8">
          {/* Logo (mobile only) */}
          <div className="flex lg:hidden items-center justify-center">
            <img src="/favicon.svg" alt="WeTook" className="h-8 w-auto" />
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-bold">{t('auth.welcomeBack')}</h2>
            <p className="text-muted-foreground text-sm">{t('auth.welcomeSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="orgNo">{t('auth.orgNo', 'Organization ID')}</Label>
              <div className="relative">
                <Building2 className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="orgNo"
                  type="text"
                  inputMode="text"
                  autoComplete="organization"
                  dir="ltr"
                  value={orgNo}
                  onChange={e => setOrgNo(e.target.value.toUpperCase())}
                  placeholder={t('auth.orgNoPlaceholder', '1XXXXXXXXX')}
                  className="ps-9 font-mono tracking-wider"
                  maxLength={12}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  tabIndex={-1}
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pe-10"
                />
                <button
                  type="button"
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={v => setRemember(v === true)}
              />
              <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                {t('auth.rememberMe')}
              </Label>
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-primary font-medium hover:underline">
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
